class ThemeScrollReveal {
  constructor() {
    // Read settings from body data attributes
    this.enabled = document.body.dataset.scrollRevealEnabled === 'true';
    this.animateOnce = document.body.dataset.scrollRevealOnce === 'true';
    this.trigger = document.body.dataset.scrollRevealTrigger || 'on-scroll';
    
    if (!this.enabled) return;
    
    this.sections = document.querySelectorAll('.shopify-section, .section');
    this.excludedSelectors = [
      '#shopify-section-header',
      '#shopify-section-announcement-bar',
      '#shopify-section-footer',
      '.shopify-section-header-sticky'
    ];
    
    this.init();
    
    // CRITICAL FIX: Listen for Theme Editor events
    this.initThemeEditorSupport();
  }

  init() {
    if (!this.sections.length) return;

    // Get MainContent element
    const mainContent = document.getElementById('MainContent');
    let firstSection = null;
    
    // Find first section safely
    if (mainContent) {
      // Try to find first shopify-section child
      firstSection = mainContent.querySelector(':scope > .shopify-section:first-child');
      
      // If not found, try first div child
      if (!firstSection) {
        firstSection = mainContent.querySelector(':scope > div:first-child');
      }
    }

    // Filter sections and ensure excluded ones are visible
    const sectionsToAnimate = Array.from(this.sections).filter((section) => {
      // CRITICAL: Check if section is one of the first 3 in MainContent
      if (firstSection && section === firstSection) {
        section.classList.add('revealed');
        section.style.opacity = '1';
        section.style.transform = 'none';
        return false;
      }
      
      // Check index position in MainContent (exclude first 2)
      if (mainContent) {
        const allMainSections = Array.from(mainContent.querySelectorAll('.shopify-section'));
        const sectionIndex = allMainSections.indexOf(section);
        // Index 0, 1 = first 2 sections (exclude them)
        if (sectionIndex >= 0 && sectionIndex < 2) {
          section.classList.add('revealed');
          section.style.opacity = '1';
          section.style.transform = 'none';
          return false;
        }
      }
      
      // Check if section should be excluded by attribute
      // Check both the section itself AND any child elements
      if (section.hasAttribute('data-scroll-reveal-exclude') || 
          section.querySelector('[data-scroll-reveal-exclude]')) {
        // Make sure excluded section is visible
        section.classList.add('revealed');
        section.style.opacity = '1';
        section.style.transform = 'none';
        return false;
      }
      
      // Check if section matches excluded selectors
      for (let selector of this.excludedSelectors) {
        if (section.matches(selector)) {
          // Make sure excluded section is visible
          section.classList.add('revealed');
          section.style.opacity = '1';
          section.style.transform = 'none';
          return false;
        }
      }
      
      return true;
    });

    // If immediate mode, reveal all sections immediately
    if (this.trigger === 'immediate') {
      // Add a small delay for staggered effect
      sectionsToAnimate.forEach((section, index) => {
        setTimeout(() => {
          section.classList.add('revealed');
        }, index * 100); // 100ms delay between each section
      });
      return; // Exit - no need for Intersection Observer
    }

    // On-scroll mode - reveal above-the-fold sections immediately
    this.revealAboveTheFold(sectionsToAnimate);
    
    // Then setup observer for below-the-fold sections
    this.setupObserver(sectionsToAnimate);
  }

  revealAboveTheFold(sections) {
    // Get viewport height
    const viewportHeight = window.innerHeight;
    
    // Reveal all sections that are already in viewport
    sections.forEach(section => {
      const rect = section.getBoundingClientRect();
      
      // Check if section is in viewport (top is above bottom of viewport)
      // We use a generous threshold to catch sections that are partially visible
      if (rect.top < viewportHeight) {
        section.classList.add('revealed');
        section.style.opacity = '1';
        section.style.transform = 'none';
      }
    });
  }

  setupObserver(sections) {
    // CRITICAL FIX: Use same approach as reference theme
    // rootMargin: '0px 0px -50px 0px' means:
    // - Trigger when section is 50px away from bottom of viewport
    // - This is MUCH earlier than -100px or -200px
    // - Prevents white gaps on all devices
    const options = {
      root: null,
      rootMargin: '0px 0px -50px 0px', // Reference theme uses -50px
      threshold: 0 // Trigger as soon as ANY part enters the trigger zone
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          
          // Stop observing if animateOnce is true
          if (this.animateOnce) {
            this.observer.unobserve(entry.target);
          }
        } else {
          // Remove revealed class if animateOnce is false
          if (!this.animateOnce) {
            entry.target.classList.remove('revealed');
          }
        }
      });
    }, options);

    // Observe all sections
    sections.forEach(section => {
      this.observer.observe(section);
    });
  }
  
  initThemeEditorSupport() {
    // CRITICAL FIX: Detect if we're in the Theme Editor
    const isThemeEditor = window.Shopify && window.Shopify.designMode;
    
    if (!isThemeEditor) return;
    
    // In Theme Editor, reveal all sections immediately to prevent blank sections
    // This ensures sections are visible when making changes
    const revealAllSections = () => {
      const allSections = document.querySelectorAll('.shopify-section:not(.revealed), .section:not(.revealed)');
      allSections.forEach(section => {
        // In Theme Editor, reveal ALL sections including excluded ones
        // This prevents blank sections when adding/unhiding sections
        section.classList.add('revealed');
        section.style.opacity = '1';
        section.style.transform = 'none';
      });
      
      // Also explicitly reveal sections that already have the revealed class
      // but might have been hidden due to inline styles
      const allRevealedSections = document.querySelectorAll('.shopify-section.revealed, .section.revealed');
      allRevealedSections.forEach(section => {
        section.style.opacity = '1';
        section.style.transform = 'none';
      });
    };
    
    // Reveal all sections immediately in editor
    revealAllSections();
    
    // Listen for section changes in Theme Editor
    document.addEventListener('shopify:section:load', () => {
      // When a section is loaded/reloaded, reveal it immediately
      setTimeout(revealAllSections, 50);
    });
    
    document.addEventListener('shopify:section:reorder', () => {
      // When sections are reordered, reveal all
      setTimeout(revealAllSections, 50);
    });
    
    document.addEventListener('shopify:section:select', () => {
      // When a section is selected, ensure it's visible
      setTimeout(revealAllSections, 50);
    });
    
    document.addEventListener('shopify:section:deselect', () => {
      // Keep sections visible after deselect
      setTimeout(revealAllSections, 50);
    });
    
    // Also use MutationObserver to catch any DOM changes
    const observer = new MutationObserver(() => {
      revealAllSections();
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ThemeScrollReveal();
  });
} else {
  new ThemeScrollReveal();
}